import { useState, useMemo } from 'react';
import { getBookings, updateBooking } from '@/utils/bookingStore';
import { getPaymentsByBooking } from '@/utils/paymentStore';
import { addActivityLog } from '@/utils/activityLogStore';
import { demoGuests, demoRooms, demoRoomBeds } from '@/mocks/demoData';
import type { Booking } from '@/mocks/data';
import { useApp } from '@/contexts/AppContext';

const STATUS_BADGES: Record<string, string> = {
  checked_in: 'bg-green-100 text-green-700',
  checked_out: 'bg-navy-100 text-navy-600',
  confirmed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
};

const TYPE_BADGES: Record<string, string> = {
  walk_in: 'bg-purple-100 text-purple-700',
  advance: 'bg-gold-100 text-gold-700',
  pre_booking: 'bg-cyan-100 text-cyan-700',
  online: 'bg-orange-100 text-orange-700',
};

export default function BookingManage() {
  const { addToast } = useApp();
  const [bookings, setBookings] = useState<Booking[]>(getBookings());
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [showCancel, setShowCancel] = useState<Booking | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showDetail, setShowDetail] = useState<Booking | null>(null);
  const [showRefund, setShowRefund] = useState<Booking | null>(null);
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundMethod, setRefundMethod] = useState('cash');
  const [refundNote, setRefundNote] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;

  const refreshBookings = () => setBookings([...getBookings()]);

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      const guest = demoGuests.find((g) => g.id === b.guest_id);
      const room = demoRooms.find((r) => r.id === b.room_id);
      const q = search.toLowerCase();
      if (search && !guest?.full_name.toLowerCase().includes(q) && !b.booking_id.toLowerCase().includes(q) && !room?.room_number.includes(q)) return false;
      if (filterStatus !== 'all' && b.status !== filterStatus) return false;
      if (filterType !== 'all' && b.booking_type !== filterType) return false;
      if (filterDate) {
        const bDate = new Date(b.check_in).toISOString().slice(0, 10);
        if (bDate !== filterDate) return false;
      }
      return true;
    });
  }, [bookings, search, filterStatus, filterType, filterDate]);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, currentPage]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const handleCancel = () => {
    if (!showCancel) return;
    if (!cancelReason) { addToast('error', 'Enter cancellation reason'); return; }
    updateBooking(showCancel.id, { status: 'cancelled', cancellation_reason: cancelReason, refund_status: 'Pending' });
    addActivityLog({
      user_id: 'usr-003',
      user_name: 'Reception',
      action: 'Booking Cancelled',
      entity_type: 'booking',
      entity_id: showCancel.id,
      details: { guest: demoGuests.find((g) => g.id === showCancel.guest_id)?.full_name, reason: cancelReason },
    });
    refreshBookings();
    addToast('success', `Booking ${showCancel.booking_id} cancelled`);
    setShowCancel(null); setCancelReason('');
  };

  const handleRefund = () => {
    if (!showRefund) return;
    if (refundAmount <= 0) { addToast('error', 'Enter refund amount'); return; }
    updateBooking(showRefund.id, { refund_status: 'Refunded', pending_amount: 0 });
    addActivityLog({
      user_id: 'usr-003',
      user_name: 'Reception',
      action: 'Refund Processed',
      entity_type: 'refund',
      entity_id: showRefund.id,
      details: { amount: refundAmount, method: refundMethod, booking: showRefund.booking_id },
    });
    refreshBookings();
    addToast('success', `Refund of Rs. ${refundAmount.toLocaleString('en-IN')} processed for ${showRefund.booking_id}`);
    setShowRefund(null); setRefundAmount(0); setRefundMethod('cash'); setRefundNote('');
  };

  const getGuest = (id: string) => demoGuests.find((g) => g.id === id);
  const getRoom = (id: string) => demoRooms.find((r) => r.id === id);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-navy-800">Manage Bookings</h1>
          <p className="text-navy-500 mt-1">View, edit, cancel, and process refunds for all reservations.</p>
        </div>
        <button onClick={refreshBookings} className="px-4 py-2 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line mr-1" /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {(['checked_in', 'confirmed', 'checked_out', 'cancelled'] as const).map((s) => (
          <div key={s} className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="text-xs text-navy-500 capitalize mb-1">{s.replace('_', ' ')}</div>
            <div className="text-2xl font-bold text-navy-800">{bookings.filter((b) => b.status === s).length}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Search</label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
              <input type="text" placeholder="Guest, booking ID, room..." value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} className="w-full pl-9 pr-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Status</label>
            <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="all">All</option><option value="checked_in">Checked In</option><option value="confirmed">Confirmed</option><option value="checked_out">Checked Out</option><option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Type</label>
            <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="all">All</option><option value="walk_in">Walk-in</option><option value="advance">Advance</option><option value="pre_booking">Pre-Booking</option><option value="online">Online</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Check-in Date</label>
            <input type="date" value={filterDate} onChange={(e) => { setFilterDate(e.target.value); setCurrentPage(1); }} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-50">
              <tr>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Booking ID</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Guest</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Room</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Booking Mode</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Beds</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Dates</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Type</th>
                <th className="text-left px-5 py-3 font-semibold text-navy-700">Status</th>
                <th className="text-right px-5 py-3 font-semibold text-navy-700">Amount</th>
                <th className="text-right px-5 py-3 font-semibold text-navy-700">Pending</th>
                <th className="text-center px-3 py-3 font-semibold text-navy-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((b) => {
                const guest = getGuest(b.guest_id);
                const room = getRoom(b.room_id);
                return (
                  <tr key={b.id} className="border-t border-navy-100 hover:bg-navy-50/50 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-navy-800">{b.booking_id}</td>
                    <td className="px-5 py-3.5">
                      <div className="text-navy-800">{guest?.full_name}</div>
                      <div className="text-xs text-navy-500">{guest?.phone}</div>
                    </td>
                    <td className="px-5 py-3.5 text-navy-600">{room?.room_number} <span className="text-xs text-navy-400">({room?.category})</span></td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${b.is_bed_booking ? 'bg-gold-100 text-gold-700' : 'bg-navy-100 text-navy-600'}`}>
                        {b.is_bed_booking ? (
                          <><i className="ri-hotel-bed-line mr-1" /> Bed</>
                        ) : (
                          <><i className="ri-home-4-line mr-1" /> Room</>
                        )}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-navy-500 text-xs">
                      {b.is_bed_booking && b.bed_ids.length > 0
                        ? b.bed_ids.map((bid) => {
                          const bed = demoRoomBeds.find((x) => x.id === bid);
                          return bed ? `B${bed.bed_number}` : '';
                        }).filter(Boolean).join(', ')
                        : '-'}
                      {b.is_bed_booking && <div className="text-navy-400">{b.bed_count} bed{b.bed_count > 1 ? 's' : ''}</div>}
                    </td>
                    <td className="px-5 py-3.5 text-navy-500">
                      <div>{new Date(b.check_in).toLocaleDateString('en-IN')}</div>
                      <div className="text-xs">to {new Date(b.check_out).toLocaleDateString('en-IN')}</div>
                    </td>
                    <td className="px-5 py-3.5"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${TYPE_BADGES[b.booking_type] || 'bg-navy-100 text-navy-600'}`}>{b.booking_type.replace('_', ' ')}</span></td>
                    <td className="px-5 py-3.5"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_BADGES[b.status] || 'bg-navy-100 text-navy-600'}`}>{b.status.replace('_', ' ')}</span></td>
                    <td className="px-5 py-3.5 text-right font-medium">Rs. {b.total_amount.toLocaleString('en-IN')}</td>
                    <td className="px-5 py-3.5 text-right"><span className={b.pending_amount > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>Rs. {b.pending_amount.toLocaleString('en-IN')}</span></td>
                    <td className="px-5 py-3.5 text-center">
                      <button onClick={() => setShowDetail(b)} className="text-navy-500 hover:text-navy-700 mr-2 cursor-pointer"><i className="ri-eye-line" /></button>
                      {b.status === 'cancelled' && b.refund_status === 'Pending' && (
                        <button
                          onClick={() => {
                            setShowRefund(b);
                            setRefundAmount(getPaymentsByBooking(b.id).reduce((s, p) => s + p.amount, 0));
                            setRefundMethod('cash');
                            setRefundNote('');
                          }}
                          className="text-purple-500 hover:text-purple-700 mr-2 cursor-pointer"
                          title="Process Refund"
                        >
                          <i className="ri-refund-2-line" />
                        </button>
                      )}
                      {['checked_in', 'confirmed'].includes(b.status) && (
                        <button onClick={() => { setShowCancel(b); setCancelReason(''); }} className="text-red-500 hover:text-red-700 cursor-pointer"><i className="ri-close-circle-line" /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-navy-100">
            <div className="text-xs text-navy-500">Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of {filtered.length}</div>
            <div className="flex gap-1">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1.5 border border-navy-200 rounded-lg text-sm disabled:opacity-40 hover:bg-navy-50 cursor-pointer"><i className="ri-arrow-left-s-line" /></button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => setCurrentPage(p)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${currentPage === p ? 'bg-navy-700 text-white' : 'border border-navy-200 hover:bg-navy-50 text-navy-600'} cursor-pointer`}>{p}</button>
              ))}
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1.5 border border-navy-200 rounded-lg text-sm disabled:opacity-40 hover:bg-navy-50 cursor-pointer"><i className="ri-arrow-right-s-line" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Modal */}
      {showCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">Cancel Booking</h3>
              <button onClick={() => setShowCancel(null)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6">
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <i className="ri-alert-line mr-1" /> You are about to cancel <strong>{showCancel.booking_id}</strong>
              </div>
              <label className="block text-sm font-medium text-navy-700 mb-1.5">Cancellation Reason *</label>
              <textarea rows={3} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button onClick={() => setShowCancel(null)} className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Keep Booking</button>
              <button onClick={handleCancel} className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer">Confirm Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefund && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">Process Refund</h3>
              <button onClick={() => setShowRefund(null)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-700">
                <i className="ri-information-line mr-1" />
                Refund for <strong>{showRefund.booking_id}</strong> — {getGuest(showRefund.guest_id)?.full_name}
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-navy-500">Cancellation Reason</span>
                <span className="text-navy-800 font-medium">{showRefund.cancellation_reason}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-navy-500">Total Paid</span>
                <span className="text-navy-800 font-medium">Rs. {getPaymentsByBooking(showRefund.id).reduce((s, p) => s + p.amount, 0).toLocaleString('en-IN')}</span>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Refund Amount (Rs.) *</label>
                <input type="number" value={refundAmount} onChange={(e) => setRefundAmount(Number(e.target.value))} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Refund Method</label>
                <select value={refundMethod} onChange={(e) => setRefundMethod(e.target.value)} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Refund Note</label>
                <textarea rows={2} value={refundNote} onChange={(e) => setRefundNote(e.target.value)} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none" placeholder="Optional note..." />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button onClick={() => setShowRefund(null)} className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Cancel</button>
              <button onClick={handleRefund} className="flex-1 px-4 py-2.5 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition-colors cursor-pointer">
                <i className="ri-refund-2-line mr-1" /> Process Refund
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">Booking Details</h3>
              <button onClick={() => setShowDetail(null)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 space-y-4">
              {(() => {
                const b = showDetail;
                const g = getGuest(b.guest_id);
                const r = getRoom(b.room_id);
                const bookingPayments = getPaymentsByBooking(b.id);
                return (
                  <>
                    <div className="flex items-center justify-between p-3 bg-navy-50 rounded-lg">
                      <span className="text-sm font-medium text-navy-700">{b.booking_id}</span>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${STATUS_BADGES[b.status]}`}>{b.status.replace('_', ' ')}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div><div className="text-xs text-navy-400 mb-1">Guest</div><div className="font-medium text-navy-800">{g?.full_name}</div><div className="text-xs text-navy-500">{g?.phone}</div></div>
                      <div><div className="text-xs text-navy-400 mb-1">Room</div><div className="font-medium text-navy-800">{r?.room_number}</div><div className="text-xs text-navy-500">{r?.category}</div></div>
                      <div><div className="text-xs text-navy-400 mb-1">Booking Mode</div><div className="font-medium text-navy-800">{b.is_bed_booking ? 'Bed Booking' : 'Full Room'}</div></div>
                      {b.is_bed_booking && b.bed_ids.length > 0 && (
                        <div><div className="text-xs text-navy-400 mb-1">Beds</div><div className="font-medium text-navy-800">
                          {b.bed_ids.map((bid) => { const bed = demoRoomBeds.find((x) => x.id === bid); return bed ? `Bed ${bed.bed_number}` : ''; }).filter(Boolean).join(', ')}
                          <span className="text-xs text-navy-400 ml-1">({b.bed_count} bed{b.bed_count > 1 ? 's' : ''})</span>
                        </div></div>
                      )}
                      <div><div className="text-xs text-navy-400 mb-1">Check-in</div><div className="font-medium text-navy-800">{new Date(b.check_in).toLocaleString('en-IN')}</div></div>
                      <div><div className="text-xs text-navy-400 mb-1">Check-out</div><div className="font-medium text-navy-800">{new Date(b.check_out).toLocaleString('en-IN')}</div></div>
                      <div><div className="text-xs text-navy-400 mb-1">Type</div><div className="font-medium text-navy-800 capitalize">{b.booking_type.replace('_', ' ')}</div></div>
                      <div><div className="text-xs text-navy-400 mb-1">Deposit</div><div className="font-medium text-navy-800">Rs. {b.deposit.toLocaleString('en-IN')}</div></div>
                      {b.refund_status && (
                        <div><div className="text-xs text-navy-400 mb-1">Refund Status</div><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${b.refund_status === 'Refunded' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{b.refund_status}</span></div>
                      )}
                    </div>
                    <div className="border-t border-navy-100 pt-4">
                      <div className="text-sm font-medium text-navy-700 mb-2">Charges</div>
                      <div className="flex justify-between text-sm"><span className="text-navy-500">Room Charges</span><span className="font-medium">Rs. {b.total_amount.toLocaleString('en-IN')}</span></div>
                      {b.extra_charges.map((e, i) => (
                        <div key={i} className="flex justify-between text-sm"><span className="text-navy-500">{e.name}</span><span className="font-medium">Rs. {e.amount.toLocaleString('en-IN')}</span></div>
                      ))}
                      <div className="flex justify-between text-sm font-semibold mt-2 pt-2 border-t border-navy-100"><span className="text-navy-800">Total</span><span className="text-navy-800">Rs. {(b.total_amount + b.extra_charges.reduce((s, e) => s + e.amount, 0)).toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between text-sm mt-1"><span className="text-navy-500">Pending</span><span className={b.pending_amount > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>Rs. {b.pending_amount.toLocaleString('en-IN')}</span></div>
                    </div>
                    {bookingPayments.length > 0 && (
                      <div className="border-t border-navy-100 pt-4">
                        <div className="text-sm font-medium text-navy-700 mb-2">Payment History</div>
                        <div className="space-y-2">
                          {bookingPayments.map((p) => (
                            <div key={p.id} className="flex items-center justify-between p-2 bg-navy-50 rounded-lg text-sm">
                              <div>
                                <div className="text-navy-700">{p.receipt_no}</div>
                                <div className="text-xs text-navy-400 capitalize">{p.payment_type} — {p.payment_method}</div>
                              </div>
                              <div className="font-medium text-navy-800">Rs. {p.amount.toLocaleString('en-IN')}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {b.cancellation_reason && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        <div className="font-medium">Cancellation Reason:</div>{b.cancellation_reason}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="px-6 py-4 border-t border-navy-100">
              <button onClick={() => setShowDetail(null)} className="w-full px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}